import { describeRuleKey } from "encamp-shared/src/waste/stateWasteCodes";
import { DiscrepancyKind, WasteManifestSource } from "generated-graphql/graphql";

import { SOURCE_LABEL, SOURCE_ORDER } from "./Manifests/manifestSourceDisplay";

/**
 * Mapping from `ManifestDiscrepancy.field` (raw string) to the human-readable
 * label shown in the waste-stream grid's per-cell tooltip. The keyset is also
 * the in-scope allowlist — fields not listed here do not surface on the grid
 * (typically manifest-level concerns like generatorName / destinationFacilityName,
 * which belong on the manifest detail page).
 *
 * Mirrors but is intentionally narrower than `FIELD_LABELS` in
 * packages/encamp-waste-data-access/src/discrepancies/chFormat.ts. Kept local
 * so the grid's surface area is explicit and divergence from the broader
 * detector catalog is easy to spot. formCode and sourceCode aren't in
 * FIELD_LABELS today (detection for them is a future concern) — defined here.
 */
export const WS_DISCREPANCY_FIELD_LABELS: Record<string, string> = {
  quantity: "Amount",
  formCode: "Form Code",
  sourceCode: "Source Code",
  managementMethodCode: "Management Method Code",
  federalWasteCodes: "Federal Waste Codes",
  stateWasteCodes: "State Waste Codes",
};

/**
 * Display labels for the non-field discrepancy kinds that surface in the
 * consolidated "Issues" column. FIELD rows resolve to a `WS_DISCREPANCY_FIELD_LABELS`
 * entry and FIELD_ISSUE rows to their rule description, so only the
 * manifest-level kinds need a fixed label here.
 */
const DISCREPANCY_KIND_LABELS: Record<string, string> = {
  EMANIFEST_MISSING: "Missing from e-Manifest",
  EPA_CORRECTION_REQUEST: "EPA correction request",
};

/**
 * Prefix applied to field-discrepancy labels in the consolidated "Issues"
 * column, so a field discrepancy ("Discrepancy-Management Method Code") reads
 * distinctly from a field-issue rule description ("TX hazardous code with no
 * federal code — likely wrong"). Also clarifies the raw-field-name fallback,
 * e.g. "Discrepancy-shippedDate" for manifest-level fields outside the grid.
 */
const FIELD_DISCREPANCY_LABEL_PREFIX = "Discrepancy-";

/**
 * Flattens a stream's discrepancies into a deduplicated, display-ordered list
 * of human-readable issue labels for the consolidated "Issues" grid column:
 * - FIELD_ISSUE rows → the rule description (TX_STATE_WASTE_CODE_RULE_DESC).
 * - manifest-level kinds (EMANIFEST_MISSING, EPA_CORRECTION_REQUEST) → a fixed
 *   label.
 * - everything else (FIELD discrepancies / unknown kinds) → the field label,
 *   prefixed with "Discrepancy-" (raw field name when unmapped).
 *
 * Field-discrepancy labels are listed before field-issue labels so the column
 * reads consistently across rows. Returns an empty array when the row has no
 * surfaceable issues.
 */
export function buildConsolidatedIssueLabels(
  discrepancies: ReadonlyArray<{ field: string; kind?: string | null; ruleKey?: string | null }>
): string[] {
  const fieldLabels: string[] = [];
  const issueLabels: string[] = [];
  const seen = new Set<string>();
  const push = (target: string[], label: string) => {
    if (seen.has(label)) return;
    seen.add(label);
    target.push(label);
  };

  for (const d of discrepancies) {
    if (d.kind === DiscrepancyKind.FieldIssue && d.ruleKey) {
      push(issueLabels, describeRuleKey(d.ruleKey));
      continue;
    }
    const kindLabel = d.kind ? DISCREPANCY_KIND_LABELS[d.kind] : undefined;
    if (kindLabel) {
      push(fieldLabels, kindLabel);
      continue;
    }
    push(
      fieldLabels,
      `${FIELD_DISCREPANCY_LABEL_PREFIX}${WS_DISCREPANCY_FIELD_LABELS[d.field] ?? d.field}`
    );
  }

  return [...fieldLabels, ...issueLabels];
}

/**
 * Counts how many of the supplied discrepancies target a given
 * `ManifestDiscrepancy.field` value. The `discrepancies` array on each
 * waste-stream row is small in the realistic case, so a linear filter is the
 * right shape; no memoization needed.
 */
export function countDiscrepanciesOnField(
  discrepancies: ReadonlyArray<{ field: string }>,
  field: string
): number {
  let count = 0;
  for (const d of discrepancies) {
    if (d.field === field) count++;
  }
  return count;
}

/**
 * True when at least two non-null source values are present and not all equal.
 * Missing (null/undefined) values are ignored — a single reported source is
 * not a "disagreement". Intended for primitive comparisons (string, number).
 *
 * This is the silent-disagreement signal: a field whose sources differ but
 * has no ManifestDiscrepancy row attached yet. Pairs with the discrepancy
 * count to drive the cell-level indicator.
 */
export function sourcesDisagree<T>(values: ReadonlyArray<T | null | undefined>): boolean {
  const present = values.filter((v): v is T => v != null);
  if (present.length < 2) return false;
  const first = present[0];
  for (let i = 1; i < present.length; i++) {
    if (present[i] !== first) return true;
  }
  return false;
}

/**
 * Staff-only "Discrepancy" omnisearch filter column, used identically by the
 * tenant (`TenantWasteStreams`) and facility (`WasteStreamsDataGrid`) grids.
 * Defined once here so the enum values and presentation labels can't drift
 * between the two grids.
 */
export const DISCREPANCY_TYPE_FILTER_COLUMN = {
  filterKeyType: "enumAutocomplete" as const,
  key: "discrepancyType",
  header: "Discrepancy",
  enumValues: [
    "ANY",
    "NONE",
    "FIELD_ISSUE",
    "FIELD_DISCREPANCY",
    "EMANIFEST_MISSING",
    "EPA_CORRECTION_REQUEST",
  ],
  enumPresentationFunction: (value: string) =>
    ({
      ANY: "Any discrepancy",
      NONE: "No discrepancies",
      FIELD_ISSUE: "Field issue",
      FIELD_DISCREPANCY: "Field discrepancy",
      EMANIFEST_MISSING: "Missing from e-Manifest",
      EPA_CORRECTION_REQUEST: "EPA correction request",
    })[value] ?? value,
  ignoreStateAbbreviations: true,
};

/** One source's reported value for a field, for the per-cell discrepancy tooltip. */
export interface FieldDisagreementLine {
  source: WasteManifestSource;
  /** Display label for the source, e.g. "e-Manifest". */
  label: string;
  value: string;
}

/** A stream discrepancy row with the fields the per-cell indicator/tooltip reads. */
export interface WasteStreamDiscrepancyRow {
  field: string;
  kind?: string | null;
  ruleKey?: string | null;
  /** `DiscrepancyStatus` — UNRESOLVED / PENDING / RESOLVED / IGNORED. */
  status?: string | null;
  eManifestValue?: string | null;
  vendorValue?: string | null;
  manifestDocumentValue?: string | null;
}

/**
 * Discrepancy statuses that represent a live, unaddressed conflict. The
 * `paged_discrepancies` feed is intentionally status-inclusive (RESOLVED and
 * IGNORED rows are kept so a stream still counts historically), but the per-cell
 * (!) indicator must reflect only open discrepancies — otherwise a months-old
 * resolved row surfaces its frozen raise-time snapshot as if it were a current
 * conflict, next to a cell that has since been corrected.
 */
const OPEN_DISCREPANCY_STATUSES: ReadonlySet<string> = new Set(["UNRESOLVED", "PENDING"]);

/** True when a discrepancy is still open (not resolved or ignored). */
export function isOpenDiscrepancy(d: { status?: string | null }): boolean {
  return d.status != null && OPEN_DISCREPANCY_STATUSES.has(d.status);
}

/**
 * True when per-source lines represent an actual cross-source conflict: at least
 * two distinct sources reported a value. Several values from a single source
 * (e.g. two one-sided quantity discrepancies on different lines, each with only
 * an e-Manifest value) is NOT a disagreement and must not be styled as one.
 */
export function linesShowCrossSourceDisagreement(
  lines: ReadonlyArray<FieldDisagreementLine>
): boolean {
  return new Set(lines.map((line) => line.source)).size >= 2;
}

/**
 * Flattens a field's discrepancies into the distinct per-source values that
 * disagree, in canonical source order (Document → e-Manifest → Vendor). A
 * stream aggregates many manifest lines, so a field can carry several distinct
 * values per source; identical (source, value) pairs collapse. Comparison-less
 * rows (FIELD_ISSUE) carry no values and naturally drop out. Drives the rich
 * "actual discrepancy" tooltip on the grid's (!) indicator.
 */
export function collectFieldDisagreementLines(
  discrepancies: ReadonlyArray<WasteStreamDiscrepancyRow>,
  field: string
): FieldDisagreementLine[] {
  const valuesBySource = new Map<WasteManifestSource, Set<string>>();
  const add = (source: WasteManifestSource, value: string | null | undefined) => {
    if (value == null || value === "") return;
    const set = valuesBySource.get(source) ?? new Set<string>();
    set.add(value);
    valuesBySource.set(source, set);
  };
  for (const d of discrepancies) {
    if (d.field !== field) continue;
    add(WasteManifestSource.ManifestDocument, d.manifestDocumentValue);
    add(WasteManifestSource.EManifest, d.eManifestValue);
    add(WasteManifestSource.Vendor, d.vendorValue);
  }
  const lines: FieldDisagreementLine[] = [];
  for (const source of SOURCE_ORDER) {
    for (const value of valuesBySource.get(source) ?? []) {
      lines.push({ source, label: SOURCE_LABEL[source], value });
    }
  }
  return lines;
}

/**
 * Deduplicated rule descriptions for the field-issue (CE-driven) discrepancies
 * on a stream — e.g. the state-waste-code rule findings that have no per-source
 * values of their own. Listed beneath the source values in the cell tooltip.
 */
export function collectFieldIssueLabels(
  discrepancies: ReadonlyArray<Pick<WasteStreamDiscrepancyRow, "field" | "kind" | "ruleKey">>,
  field: string
): string[] {
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const d of discrepancies) {
    if (d.field === field && d.kind === DiscrepancyKind.FieldIssue && d.ruleKey) {
      const label = describeRuleKey(d.ruleKey);
      if (!seen.has(label)) {
        seen.add(label);
        labels.push(label);
      }
    }
  }
  return labels;
}

/**
 * Composes the tooltip/aria text for a field-level warning combining the
 * discrepancy count and a silent source-disagreement flag. Returns `null`
 * when there's nothing to communicate so callers can skip rendering the
 * indicator.
 */
export function buildFieldDiscrepancyWarning(
  count: number,
  fieldLabel: string,
  hasSourceDisagreement = false
): { tooltip: string; ariaLabel: string } | null {
  if (count <= 0 && !hasSourceDisagreement) return null;
  const parts: string[] = [];
  if (count > 0) {
    parts.push(`${count} ${count === 1 ? "discrepancy" : "discrepancies"}`);
  }
  if (hasSourceDisagreement) {
    parts.push("sources disagree");
  }
  const label = `${parts.join("; ")} on ${fieldLabel}`;
  return { tooltip: label, ariaLabel: label };
}
